import React, { Fragment, useEffect, useState } from "react"
import { Row, Table, Button, Col } from "reactstrap"
import { Link } from "react-router-dom"

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  useTable,
  useRowSelect,
} from "@tanstack/react-table"

import { rankItem } from "@tanstack/match-sorter-utils"
import JobListGlobalFilter from "./GlobalSearchFilter"
import StatusFilter from "./StatusFilter"
import DebouncedInput from "./DebouncedInput "
import Spinners from "components/Common/Spinner"

// Column Filter
const Filter = ({ column }) => {
  const columnFilterValue = column.getFilterValue()

  return (
    <>
      <DebouncedInput
        type="text"
        value={columnFilterValue ?? ""}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  )
}

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  isJobListGlobalFilter,
  statusOptions,
  statusFilterfn,
  selectedValue,
  CustomPageSize,
  isLoading,
}) => {
  //
  const [columnFilters, setColumnFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("1,4")
  const [options, setOptions] = useState(statusOptions)

  const [rowSelection, setRowSelection] = useState({})
  // Define el reducer para manejar las acciones de selección
  const rowSelectionReducer = (state, action) => {
    switch (action.type) {
      case "toggle_selection":
        return { ...state, [action.rowId]: action.checked }
      case "clear_selection":
        return {}
      default:
        return state
    }
  }

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({
      itemRank,
    })
    return itemRank.passed
  }

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      rowSelection,
    },
    enableRowSelection: true, //enable row selection for all rows
    // enableRowSelection: row => row.original.age > 18, // or enable row selection conditionally per row
    onRowSelectionChange: newSelection => {
      // Opcional: manejar los cambios de selección aquí
      //console.log("Nueva selección:", newSelection)
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  })

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
    getIsAllRowsSelected,
    getToggleAllRowsSelectedHandler,
  } = table

  useEffect(() => {
    if (CustomPageSize > 0) table.setPageSize(Number(CustomPageSize))
  }, [])

  return (
    <Fragment>
      {isLoading ? (
        <>
          <Spinners setLoading={() => {}} />
        </>
      ) : (
        <></>
      )}
      <Row className="mb-2">
        {isCustomPageSize && !CustomPageSize && (
          <Col sm={2}>
            <select
              className="form-select pageSize mb-2"
              value={table.getState().pagination.pageSize}
              onChange={e => {
                table.setPageSize(Number(e.target.value))
              }}
            >
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}
        {isGlobalFilter && (
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={value => setGlobalFilter(String(value))}
            className="form-control search-box me-2 mb-2 d-inline-block"
            placeholder={SearchPlaceholder}
          />
        )}
        {isJobListGlobalFilter && (
          <StatusFilter
            setStatusFilter={handleUserClick}
            options={options}
            selectedDefaultValue={selectedValue}
          />
        )}
        {isAddButton && (
          <Col className="d-flex justify-content-end">
            <div className="text-sm-end">
              <Button
                type="button"
                className={buttonClass}
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus me-1"></i> {buttonName}
              </Button>
            </div>
          </Col>
        )}
      </Row>
      <div className={divClassName ? divClassName : "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`${
                        header.column.columnDef.enableSorting
                          ? "sorting sorting_desc"
                          : ""
                      }`}
                    >
                      {header.isPlaceholder ? null : (
                        <React.Fragment>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map(row => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </Table>
      </div>
      {/* {console.log(table.getSelectedRowModel())} */}
      {isPagination && (
        <Row>
          <Col sm={12} md={5}>
            <div className="dataTables_info">
              {/* Showing {getState().pagination.pageSize} of {data.length} Results */}
              Showing{" "}
              {getState().pagination.pageSize <
              table.getFilteredRowModel().rows.length
                ? getState().pagination.pageSize
                : table.getFilteredRowModel().rows.length}{" "}
              of {table.getFilteredRowModel().rows.length} Results
            </div>
          </Col>
          <Col sm={12} md={7}>
            <div className={paginationWrapper}>
              <ul className={pagination}>
                <li
                  className={`paginate_button page-item previous ${
                    !getCanPreviousPage() ? "disabled" : ""
                  }`}
                >
                  <Link to="#" className="page-link" onClick={previousPage}>
                    <i className="mdi mdi-chevron-left"></i>
                  </Link>
                </li>

                {getPageOptions()
                  .filter(item => {
                    const currentPage = getState().pagination.pageIndex
                    const totalPages = getPageOptions().length

                    // Show first, last, current page, and pages around current page
                    return (
                      item === 0 ||
                      item === totalPages - 1 ||
                      (item >= currentPage - 1 && item <= currentPage + 1) ||
                      (item <= 3 && currentPage <= 3) ||
                      (item >= totalPages - 4 && currentPage >= totalPages - 4)
                    )
                  })
                  .map((item, key, array) => {
                    const isSeparatorBefore =
                      key > 0 && item - array[key - 1] > 1
                    const isSeparatorAfter =
                      key < array.length - 1 && array[key + 1] - item > 1

                    return (
                      <React.Fragment key={key}>
                        {isSeparatorBefore && (
                          <li className="page-item">...</li>
                        )}
                        <li
                          className={`paginate_button page-item ${
                            getState().pagination.pageIndex === item
                              ? "active"
                              : ""
                          }`}
                        >
                          <Link
                            to="#"
                            className="page-link"
                            onClick={() => setPageIndex(item)}
                          >
                            {item + 1}
                          </Link>
                        </li>
                        {isSeparatorAfter && <li className="page-item">...</li>}
                      </React.Fragment>
                    )
                  })}

                <li
                  className={`paginate_button page-item next ${
                    !getCanNextPage() ? "disabled" : ""
                  }`}
                >
                  <Link to="#" className="page-link" onClick={nextPage}>
                    <i className="mdi mdi-chevron-right"></i>
                  </Link>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  )
}

export default TableContainer
